import { toast } from 'sonner';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DoorOpen, KeyRound, ExternalLink, LogOut } from 'lucide-react';
import rentalsService from '../../services/rentalsService';
import apiClient from '../../utils/apiClient';
import RentalHoldModal from '../RentalHoldModal';

/**
 * RentalsInlineSection — ESS parity on the Customer detail page.
 *
 * Lists every rental this customer has (Active / Delinquent / Ended) inline
 * so the operator never has to bounce to the Rentals tab to see what the
 * tenant is renting. Data comes from GET /api/facilities/{fid}/customers/{id}
 * — the customer endpoint denormalizes the per-rental unit join server-side
 * (one find for the unit lookup, one for the type-rate map) so this
 * component is render-only.
 *
 * Columns mirror ESS: Unit | Size | Status | Monthly Rate | Next Bill |
 * Paid To | Move-in | Gate Code | Actions.
 *
 * Gate Code is per-customer, not per-rental (Falcon XT mental model: code =
 * person, not unit), so we accept it as a prop and render the same value on
 * every row.
 */

const fmtMoney = (n) => {
  const v = Number(n || 0);
  return `$${v.toFixed(2)}`;
};

const fmtDate = (iso) => {
  if (!iso) return '—';
  // Accept either YYYY-MM-DD or full ISO. Strip the time half if present.
  const s = String(iso).slice(0, 10);
  // Render as M/D/YYYY for the US-storage operator audience.
  const [y, m, d] = s.split('-');
  if (!y || !m || !d) return s;
  return `${parseInt(m, 10)}/${parseInt(d, 10)}/${y}`;
};

const statusPill = (status) => {
  const s = String(status || '').toLowerCase();
  if (s === 'active') {
    return 'bg-emerald-50 text-emerald-700 border-emerald-200';
  }
  if (s === 'delinquent') {
    return 'bg-rose-50 text-rose-700 border-rose-200';
  }
  if (s === 'lien') {
    return 'bg-amber-50 text-amber-700 border-amber-200';
  }
  if (s === 'ended') {
    return 'bg-slate-100 text-slate-600 border-slate-200';
  }
  return 'bg-slate-50 text-slate-500 border-slate-200';
};

const RentalsInlineSection = ({
  rentals = [],
  gateCode = '',
  facilityId = null,
  onChanged = null,
}) => {
  const navigate = useNavigate();

  // Transfer / payment-plan tools (Tier-2 collections+ops).
  const [toolModal, setToolModal] = useState(null); // {kind, rental}
  // { mode: 'pause' | 'resume', rental } — hold while a move-out is verified.
  const [holdModal, setHoldModal] = useState(null);
  const [availUnits, setAvailUnits] = useState([]);
  const [toolForm, setToolForm] = useState({});
  const [toolBusy, setToolBusy] = useState(false);

  const openTool = async (kind, rental) => {
    setToolForm({});
    setToolModal({ kind, rental });
    if (kind === 'transfer') {
      try {
        const res = await apiClient.get(`/api/facilities/${facilityId}/units/`);
        const rows = Array.isArray(res.data) ? res.data : res.data?.units || [];
        setAvailUnits(rows.filter((u) => u.status === 'Available' && !u.is_archived));
      } catch { setAvailUnits([]); }
    }
  };

  const runTool = async () => {
    const { kind, rental } = toolModal;
    setToolBusy(true);
    try {
      if (kind === 'transfer') {
        if (!toolForm.new_unit_id) { toast.error('Pick the destination unit.'); setToolBusy(false); return; }
        const res = await apiClient.post(
          `/api/facilities/${facilityId}/rentals/${rental._id}/transfer`,
          { new_unit_id: toolForm.new_unit_id,
            ...(toolForm.new_rate ? { new_rate: parseFloat(toolForm.new_rate) } : {}) });
        const adj = res.data.adjustment;
        toast.success(`Transferred to unit ${res.data.to_unit_number}.` +
          (adj > 0 ? ` Adjustment invoiced: $${adj}` : adj < 0 ? ` Credit added: $${-adj}` : ''));
      } else if (kind === 'plan') {
        const rows = (toolForm.installments || []).filter((i) => i.due_date && i.amount);
        if (!rows.length) { toast.error('Add at least one installment.'); setToolBusy(false); return; }
        await apiClient.post(
          `/api/facilities/${facilityId}/rentals/${rental._id}/payment-plan`,
          { installments: rows.map((i) => ({ due_date: i.due_date, amount: parseFloat(i.amount) })) });
        toast.success('Payment plan saved — late fees pause while it\'s honored.');
      }
      setToolModal(null);
      if (onChanged) onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Action failed.');
    } finally {
      setToolBusy(false);
    }
  };
  const [endingId, setEndingId] = useState(null);

  if (!rentals.length) {
    return (
      <div className="bg-white shadow rounded-lg mb-4 overflow-hidden">
        <div className="px-5 py-3.5 border-b border-slate-100 flex items-center gap-3">
          <DoorOpen className="h-4 w-4 text-slate-500" />
          <div>
            <h3 className="text-[14px] font-semibold text-slate-900">Rentals</h3>
            <p className="text-[11px] text-slate-500 mt-0.5">Units this customer rents.</p>
          </div>
        </div>
        <div className="px-5 py-6 text-center text-[13px] text-slate-400">
          No rentals on this customer yet.
        </div>
      </div>
    );
  }

  const doEnd = async (rentalId, force) => {
    await rentalsService.end(facilityId, rentalId, { force });
    if (typeof onChanged === 'function') onChanged();
    else window.location.reload();
  };

  const handleMoveOut = async (rentalId, unitNumber) => {
    if (!facilityId || !rentalId) return;
    // eslint-disable-next-line no-alert
    if (!window.confirm(
      `Move out unit ${unitNumber || '—'}? This ends the rental and frees the unit.`,
    )) return;
    setEndingId(rentalId);
    try {
      // Same end-rental path the Rentals + Unit Detail pages use.
      await doEnd(rentalId, false);
    } catch (err) {
      // 409 = the rental still has a balance or a credit. Offer to force the
      // move-out (acknowledge the balance now; settle/void/refund separately).
      if (err?.response?.status === 409) {
        const detail = err?.response?.data?.detail
          || 'This rental has an outstanding balance or credit.';
        // A lien 409 is a different question from a balance 409: closing a
        // legal process needs its own yes, not the balance confirmation.
        if (/lien/i.test(detail)) {
          // eslint-disable-next-line no-alert
          if (window.confirm(
            `${detail}

Close the lien workflow and move this tenant out?`,
          )) {
            try {
              await rentalsService.end(facilityId, rentalId,
                { force: true, closeLien: true });
              if (typeof onChanged === 'function') onChanged();
              else window.location.reload();
            } catch (e3) {
              toast.error(e3?.response?.data?.detail || 'Could not move out.');
            }
          }
          setEndingId(null);
          return;
        }
        // eslint-disable-next-line no-alert
        if (window.confirm(`${detail}\n\nForce the move-out anyway?`)) {
          try {
            await doEnd(rentalId, true);
          } catch (err2) {
            // eslint-disable-next-line no-alert
            alert(err2?.response?.data?.detail || 'Failed to move out this rental.');
          }
        }
      } else {
        // eslint-disable-next-line no-alert
        alert(err?.response?.data?.detail || 'Failed to move out this rental.');
      }
    } finally {
      setEndingId(null);
    }
  };

  return (
    <div className="bg-white shadow rounded-lg mb-4 overflow-hidden">
      <div className="px-5 py-3.5 border-b border-slate-100 flex items-center gap-3">
        <DoorOpen className="h-4 w-4 text-slate-500" />
        <div className="flex-1">
          <h3 className="text-[14px] font-semibold text-slate-900">
            Rentals
            <span className="ml-2 text-[11px] font-medium text-slate-400">
              ({rentals.length})
            </span>
          </h3>
          <p className="text-[11px] text-slate-500 mt-0.5">
            Units this customer rents. Click a unit number to open its detail page.
          </p>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-full text-[13px]">
          <thead className="bg-slate-50 text-slate-500">
            <tr>
              <th className="text-left font-medium px-4 py-2.5">Unit</th>
              <th className="text-left font-medium px-4 py-2.5">Size</th>
              <th className="text-left font-medium px-4 py-2.5">Status</th>
              <th className="text-right font-medium px-4 py-2.5">Monthly Rate</th>
              <th className="text-left font-medium px-4 py-2.5">Next Bill</th>
              <th className="text-left font-medium px-4 py-2.5">Bill To</th>
              <th className="text-left font-medium px-4 py-2.5">Paid To</th>
              <th className="text-right font-medium px-4 py-2.5" title="Prior balance brought forward (carryover, no invoice)">Prior bal.</th>
              <th className="text-left font-medium px-4 py-2.5">Move-in</th>
              <th className="text-left font-medium px-4 py-2.5">Move-out</th>
              <th className="text-left font-medium px-4 py-2.5">Gate Code</th>
              <th className="text-right font-medium px-4 py-2.5">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {rentals.map((r) => (
              <tr key={r._id} className="hover:bg-slate-50/60">
                <td className="px-4 py-2.5">
                  {r.unit_id ? (
                    <button
                      type="button"
                      onClick={() => navigate(`/units/${r.unit_id}`)}
                      className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-700 hover:underline font-medium"
                    >
                      {r.unit_number || '—'}
                      <ExternalLink className="h-3 w-3" />
                    </button>
                  ) : (
                    <span className="text-slate-400">—</span>
                  )}
                </td>
                <td className="px-4 py-2.5 text-slate-700">
                  {r.unit_size || r.unit_type || '—'}
                </td>
                <td className="px-4 py-2.5">
                  <span
                    className={`inline-block px-2 py-0.5 text-[11px] font-semibold rounded-full border ${statusPill(r.status)}`}
                  >
                    {r.status || '—'}
                  </span>
                  {r.paused_at && (
                    <span
                      className="ml-1 inline-block px-2 py-0.5 text-[11px] font-semibold rounded-full border bg-amber-50 text-amber-700 border-amber-200"
                      title={`Move-out reported for ${r.move_out_reported_on || '—'}. No billing or messages until this rental is moved out or resumed.`}
                    >
                      Paused
                    </span>
                  )}
                </td>
                <td className="px-4 py-2.5 text-right text-slate-900 font-medium">
                  {fmtMoney(r.monthly_rate)}
                </td>
                <td className="px-4 py-2.5 text-slate-600">{fmtDate(r.next_bill_date)}</td>
                <td className="px-4 py-2.5 text-slate-600">{fmtDate(r.billed_to)}</td>
                <td className="px-4 py-2.5 text-slate-600">{fmtDate(r.paid_to)}</td>
                <td className="px-4 py-2.5 text-right text-slate-700 font-medium">
                  {r.initial_balance > 0
                    ? fmtMoney(r.initial_balance)
                    : <span className="text-slate-300">—</span>}
                </td>
                <td className="px-4 py-2.5 text-slate-600">{fmtDate(r.start_date)}</td>
                <td className="px-4 py-2.5 text-slate-600">{fmtDate(r.end_date || r.ended_at)}</td>
                <td className="px-4 py-2.5">
                  {gateCode ? (
                    <span className="inline-flex items-center gap-1 text-slate-700 font-mono">
                      <KeyRound className="h-3 w-3 text-slate-400" />
                      {gateCode}
                    </span>
                  ) : (
                    <span className="text-slate-400">—</span>
                  )}
                </td>
                <td className="px-4 py-2.5 text-right">
                  <div className="inline-flex items-center gap-2 justify-end">
                    <button
                      type="button"
                      onClick={() => navigate(`/rentals/${r._id}/edit`)}
                      className="text-blue-600 hover:text-blue-700 hover:underline text-[12px] font-medium"
                    >
                      Edit
                    </button>
                    {String(r.status || '').toLowerCase() !== 'ended' && (
                      <button
                        type="button"
                        onClick={() => handleMoveOut(r._id, r.unit_number)}
                        disabled={endingId === r._id}
                        className="inline-flex items-center gap-1 text-rose-600 hover:text-rose-700 hover:underline text-[12px] font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <LogOut className="h-3 w-3" />
                        {endingId === r._id ? 'Moving out…' : 'Move Out'}
                      </button>
                    )}
                    {['Active', 'Delinquent'].includes(r.status) && (
                      r.paused_at ? (
                        <button type="button" onClick={() => setHoldModal({ mode: 'resume', rental: r })}
                                className="text-emerald-600 hover:text-emerald-700 hover:underline text-[12px] font-medium">
                          Resume
                        </button>
                      ) : (
                        <button type="button" onClick={() => setHoldModal({ mode: 'pause', rental: r })}
                                className="text-amber-600 hover:text-amber-700 hover:underline text-[12px] font-medium">
                          Pause
                        </button>
                      )
                    )}
                    {String(r.status || '').toLowerCase() !== 'ended' && (
                      <>
                        <button type="button" onClick={() => openTool('transfer', r)}
                                className="text-slate-500 hover:text-blue-600 hover:underline text-[12px] font-medium">
                          Transfer
                        </button>
                        {(r.balance || 0) > 0 && (
                          <>
                            <button type="button"
                                    onClick={() => openTool('plan', { ...r })}
                                    className="text-slate-500 hover:text-blue-600 hover:underline text-[12px] font-medium">
                              {r.payment_plan ? 'Plan…' : 'Plan'}
                            </button>
                            {/* Write-off fully retired 2026-08-06: forced
                                Move Out closes the ledger (voids + tags
                                written_off); per-invoice void on the Billing
                                tab covers a staying tenant. */}
</>
                        )}
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {toolModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 px-4"
             onClick={() => setToolModal(null)}>
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-5"
               onClick={(e) => e.stopPropagation()}>
            {toolModal.kind === 'transfer' && (
              <>
                <h3 className="text-[14px] font-semibold text-slate-900 mb-1">
                  Transfer unit {toolModal.rental.unit_number}
                </h3>
                <p className="text-[12px] text-slate-500 mb-3">
                  Units swap, the rate updates, and the rest of this month's rate
                  difference is invoiced (upgrade) or credited (downgrade).
                </p>
                <label className="block text-[12px] font-medium text-slate-600 mb-1">Destination unit</label>
                <select className="w-full border border-slate-300 rounded-md px-3 py-1.5 text-[13px] mb-3"
                        value={toolForm.new_unit_id || ''}
                        onChange={(e) => setToolForm({ ...toolForm, new_unit_id: e.target.value })}>
                  <option value="">Select available unit…</option>
                  {availUnits.map((u) => (
                    <option key={u._id} value={u._id}>
                      {u.unit_number} ({u.unit_type || '—'}) — ${u.rate_override || u.monthly_rate || '?'}/mo
                    </option>
                  ))}
                </select>
                <label className="block text-[12px] font-medium text-slate-600 mb-1">
                  Rate override (optional — defaults to the unit's rate)
                </label>
                <input type="number" min="0" step="0.01"
                       className="w-full border border-slate-300 rounded-md px-3 py-1.5 text-[13px] mb-3"
                       value={toolForm.new_rate || ''}
                       onChange={(e) => setToolForm({ ...toolForm, new_rate: e.target.value })} />
              </>
            )}
            {toolModal.kind === 'plan' && (
              <>
                <h3 className="text-[14px] font-semibold text-slate-900 mb-1">
                  Payment plan — unit {toolModal.rental.unit_number} (${(toolModal.rental.balance || 0).toFixed(2)} owed)
                </h3>
                <p className="text-[12px] text-slate-500 mb-3">
                  Late fees pause while the plan is honored; a missed installment
                  resumes the normal cascade automatically.
                </p>
                {[0, 1, 2, 3].map((i) => (
                  <div key={i} className="flex items-center gap-2 mb-2">
                    <input type="date"
                           className="border border-slate-300 rounded-md px-2 py-1 text-[13px]"
                           value={toolForm.installments?.[i]?.due_date || ''}
                           onChange={(e) => {
                             const rows = [...(toolForm.installments || [])];
                             rows[i] = { ...(rows[i] || {}), due_date: e.target.value };
                             setToolForm({ ...toolForm, installments: rows });
                           }} />
                    <input type="number" min="0" step="0.01" placeholder="Amount"
                           className="w-28 border border-slate-300 rounded-md px-2 py-1 text-[13px]"
                           value={toolForm.installments?.[i]?.amount || ''}
                           onChange={(e) => {
                             const rows = [...(toolForm.installments || [])];
                             rows[i] = { ...(rows[i] || {}), amount: e.target.value };
                             setToolForm({ ...toolForm, installments: rows });
                           }} />
                  </div>
                ))}
              </>
            )}
            <div className="flex justify-end gap-2 mt-2">
              <button type="button" onClick={() => setToolModal(null)}
                      className="px-3.5 py-2 rounded-lg border border-slate-300 bg-slate-200 text-[13px] font-medium text-slate-700 hover:bg-slate-300">
                Cancel
              </button>
              <button type="button" onClick={runTool} disabled={toolBusy}
                      className="px-3.5 py-2 rounded-lg bg-blue-600 text-white text-[13px] font-medium hover:bg-blue-700 disabled:opacity-50">
                {toolBusy ? 'Working…' : 'Confirm'}
              </button>
            </div>
          </div>
        </div>
      )}
      {holdModal && (
        <RentalHoldModal
          mode={holdModal.mode}
          rental={holdModal.rental}
          facilityId={facilityId}
          onClose={() => setHoldModal(null)}
          onDone={() => {
            if (typeof onChanged === 'function') onChanged();
            else window.location.reload();
          }}
        />
      )}
    </div>
  );
};

export default RentalsInlineSection;
